import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const ApiDocumentationModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <SafeIcon icon={FiIcons.FiBook} className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">API Dokumentasjon</h2>
              <p className="text-sm text-gray-500">Integrasjonsguide for utviklere</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <SafeIcon icon={FiIcons.FiX} className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Introduction */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Introduksjon</h3>
            <p className="text-gray-600 leading-relaxed">
              Vårt API lar deg integrere varslingssystemet direkte i dine eksisterende verktøy og intranett.
              All kommunikasjon skjer over HTTPS og data er kryptert.
            </p>
          </section>

          {/* Authentication */}
          <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiKey} className="w-5 h-5 text-blue-600" />
              Autentisering
            </h3>
            <p className="text-gray-600 mb-4">
              Inkluder din API-nøkkel i headeren på alle forespørsler:
            </p>
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <code className="text-green-400 font-mono text-sm">
                Authorization: Bearer DIN_API_NØKKEL
              </code>
            </div>
          </section>

          {/* Endpoints */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Endepunkter</h3>
            
            <div className="space-y-6">
              {/* Submit Report */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold font-mono">POST</span>
                    <code className="text-sm font-mono text-gray-700">/api/v1/whistleblower/report</code>
                  </div>
                  <span className="text-sm text-gray-500">Send inn nytt varsel</span>
                </div>
                <div className="p-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Request Body (JSON)</h4>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm font-mono overflow-x-auto">
{`{
  "description": "Beskrivelse av hendelsen...",
  "category": "hms", // hms, etikk, økonomi, annet
  "department_id": "dept_123",
  "is_anonymous": true,
  "incident_date": "2024-03-20"
}`}
                  </pre>
                </div>
              </div>

              {/* Get Status */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold font-mono">GET</span>
                    <code className="text-sm font-mono text-gray-700">/api/v1/cases/status/:id</code>
                  </div>
                  <span className="text-sm text-gray-500">Hent status på en sak</span>
                </div>
                <div className="p-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Response (JSON)</h4>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm font-mono overflow-x-auto">
{`{
  "id": "case_123",
  "status": "under_behandling",
  "updated_at": "2024-03-21T10:30:00Z",
  "messages": []
}`}
                  </pre>
                </div>
              </div>
            </div>
          </section>

          {/* Rate Limiting */}
          <section className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
            <h3 className="text-lg font-semibold text-yellow-900 mb-2 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiAlertCircle} className="w-5 h-5 text-yellow-600" />
              Begrensninger
            </h3>
            <p className="text-yellow-800 text-sm">
              API-et har en begrensning på 100 forespørsler per minutt per IP-adresse.
              Ved overskridelse vil du motta en 429 Too Many Requests respons.
            </p>
          </section>
        </div>
      </motion.div>
    </div>
  );
};

export default ApiDocumentationModal;